import React, { useState } from 'react';
import { X, Save } from 'lucide-react';

const EditProductModal = ({ isOpen, onClose, product, onSuccess }) => {
  const [modelName, setModelName] = useState(product?.model_name || '');
  const [isActive, setIsActive] = useState(product?.is_active ?? true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !product) return null;

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${product.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            model_name: modelName,
            is_active: isActive
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert('✓ Product updated successfully!');
        onSuccess();
        onClose();
      } else {
        setError(data.detail || 'Failed to update product');
      }
    } catch (err) {
      console.error('Error updating product:', err);
      setError('Failed to update product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-primary px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-primary-foreground">
              Edit Product
            </h3>
            <button
              onClick={onClose}
              className="text-primary-foreground hover:text-primary-foreground/80"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            <div className="space-y-4">
              {/* Model Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Model Name
                </label>
                <input
                  type="text"
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* SKU Code (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  SKU Code
                </label>
                <input
                  type="text"
                  value={product.sku_code}
                  disabled
                  className="w-full px-3 py-2 border border-input rounded-md bg-muted text-muted-foreground"
                />
              </div>

              {/* Status Toggle */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <div className="font-medium text-foreground">Product Status</div>
                  <div className="text-sm text-muted-foreground">
                    {isActive ? 'Product is currently active' : 'Product is currently inactive'}
                  </div>
                </div>
                <button
                  onClick={() => setIsActive(!isActive)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isActive ? 'bg-success' : 'bg-muted-foreground'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Live Date (Read-only) */}
              {product.live_date && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Live Date
                  </label>
                  <input
                    type="text"
                    value={new Date(product.live_date).toLocaleString()}
                    disabled
                    className="w-full px-3 py-2 border border-input rounded-md bg-muted text-muted-foreground"
                  />
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-4 bg-danger/10 border border-danger/20 text-danger px-4 py-3 rounded">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-muted px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
            <button
              onClick={handleSubmit}
              disabled={loading || !modelName.trim()}
              className="w-full inline-flex justify-center items-center gap-2 rounded-md border border-transparent shadow-sm px-4 py-2 bg-success text-success-foreground hover:bg-success/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-success sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-white text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProductModal;
